/* eslint-disable fp/no-mutation */
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Alert,
  Box,
  Button,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import React, { ReactElement, useState, useEffect } from 'react'
import axios from 'src/utilities/http-client'
import { useAppDispatch } from 'src/store/hooks'
import { fetchClasses } from 'src/v3/create/create.slice'
import { RightArrowIcon } from 'src/v3/core/icons'
import { ClassUpdated as Class, Course } from 'src/v3/core/types'
import ImageUpload from 'src/v3/components/upload-image'
import { uploadImageToS3 } from 'src/v3/utilities/s3'

interface Props {
  selectedClass?: Class
  course?: Course
  setStep: React.Dispatch<
    React.SetStateAction<
      | 'basic-info'
      | 'title-screen'
      | 'content'
      | 'quiz'
      | 'storyboard'
      | 'preview'
    >
  >
  onClassCreated?: (cls: any) => void
}

export default ({
  selectedClass,
  course,
  setStep,
  onClassCreated,
}: Props): ReactElement => {
  let dispatch = useAppDispatch()

  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [thumbnail, setThumbnail] = useState<File | undefined>(undefined)

  // Debug thumbnail state changes
  useEffect(() => {}, [thumbnail])

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required('Class name is required'),
      description: yup.string().required('Description is required'),
    }),
    enableReinitialize: true,
    initialValues: {
      name: selectedClass?.name || '',
      description: selectedClass?.description || '',
    },
    onSubmit: async (values) => {
      if (!course?.id) {
        setErrorMessage('Course is required to create a class')
        return
      }

      setLoading(true)
      try {
        let savedClass
        if (selectedClass) {
          // Update existing class
          let response = await axios.put('/api/v2/create/class', {
            id: selectedClass.id,
            name: values.name,
            description: values.description,
          })
          savedClass = response.data
        } else {
          // Create new class
          let response = await axios.post('/api/v2/create/class', {
            name: values.name,
            description: values.description,
            courseID: course.id,
            sequence: 0, // Will be updated by backend based on existing classes
          })
          savedClass = response.data
        }

        // Upload thumbnail if provided
        let classIdForUpload = savedClass?.id || selectedClass?.id
        if (thumbnail && classIdForUpload) {
          try {
            // Convert File to base64 string
            let thumbnailBase64 = await new Promise<string>((resolve) => {
              let reader = new FileReader()
              reader.onloadend = () => {
                resolve(reader.result as string)
              }
              reader.readAsDataURL(thumbnail)
            })

            // Get signed URL and upload to S3
            let uploadResponse = await axios.put('/api/v2/create/class/image', {
              id: classIdForUpload,
            })

            await uploadImageToS3(thumbnailBase64, uploadResponse.data)
          } catch (uploadError) {
            setErrorMessage(
              `Error uploading thumbnail: ${(uploadError as any)?.message}`,
            )
          }
        } else {
          // do nothing
        }

        await dispatch(fetchClasses())

        // If we just created a new class, call the callback to handle step progression
        if (!selectedClass && savedClass && onClassCreated) {
          onClassCreated(savedClass)
          return
        }

        // For editing existing classes, just go to next step
        setStep('title-screen')
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error saving class',
        )
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 3 }}>
        Class Information
      </Typography>

      <form onSubmit={form.handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {course && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Creating class for course: <strong>{course.name}</strong>
            </Alert>
          )}

          {!course && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Course information is required to create a class.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Class Name"
            name="name"
            value={form.values.name}
            onChange={form.handleChange}
            error={form.touched.name && !!form.errors.name}
            helperText={
              form.touched.name && form.errors.name
                ? form.errors.name
                : 'Name for this class within the course'
            }
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            multiline
            rows={4}
            value={form.values.description}
            onChange={form.handleChange}
            error={form.touched.description && !!form.errors.description}
            helperText={
              form.touched.description && form.errors.description
                ? form.errors.description
                : 'Brief description of what students will learn in this class'
            }
            disabled={loading}
          />

          {/* Class Thumbnail Upload */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Class Thumbnail
            </Typography>
            <ImageUpload
              type="thumbnail"
              image={thumbnail}
              setImage={setThumbnail}
              existingImage={selectedClass?.imageName}
              imageBasePath="public"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={loading || !course}
              endIcon={<RightArrowIcon />}
            >
              {selectedClass ? 'Update & Continue' : 'Create & Continue'}
            </Button>
          </Box>
        </Box>
      </form>

      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
